import React, { useState } from 'react';
import PrintInvoiceModal from './PrintInvoiceModal';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import { Wifi, WifiOff, Eye, FileText, Edit, Key, CircleDollarSign, CheckCircle, ToggleLeft, ToggleRight, Trash2, AlertTriangle, MoreHorizontal, ArrowUp, ArrowDown, ArrowUpDown, Server, Shield, ShieldOff, Ban, MapPin, MapPinOff, CheckCircle2, XCircle, Clock, Globe, BadgeCheck, BadgeAlert, Signal, SignalZero, SquareCheck, SquareX, Check, X, EllipsisVertical, Sparkles, Printer, Radio, CalendarDays, Router } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import WaInactiveBadge from './WaInactiveBadge';

/**
 * Sortable header cell helper - icon-aware.
 */
const SortHeader = ({ sortConfig, sortKey, label, onSort, className = '' }) => (
  <th
    className={`text-left py-2 px-2 text-xs font-semibold text-slate-600 dark:text-white cursor-pointer hover:bg-slate-200 transition-colors bg-slate-100 dark:bg-slate-800 ${className}`}
    onClick={() => onSort(sortKey)}
  >
    <div className="flex items-center gap-1 whitespace-nowrap">
      {label}
      {sortConfig.key === sortKey ? (
        sortConfig.direction === 'asc'
          ? <ArrowUp className="w-3 h-3 text-indigo-600" />
          : <ArrowDown className="w-3 h-3 text-indigo-600" />
      ) : (
        <ArrowUpDown className="w-3 h-3 text-slate-400" />
      )}
    </div>
  </th>
);

/**
 * Memoized single row for the customers desktop table.
 * Re-renders only when its own slice of state changes — not when other rows or
 * unrelated parent state (search input, modal toggles) update.
 */

// 2026-07-28: Inline quick date picker untuk kolom Jatuh Tempo
// Klik icon calendar di kolom → popover berisi calendar → pilih tanggal → auto-save
const QuickDueDatePicker = ({ customer, updateCustomerLocal }) => {
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  // 2026-07-28: Parse date-only string ("YYYY-MM-DD") secara LOCAL, hindari UTC parsing
  const _parseLocalDate = (v) => {
    if (!v) return new Date();
    if (v instanceof Date) return v;
    const s = String(v).slice(0, 10);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (m) return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    return new Date(s);
  };
  const [selectedDate, setSelectedDate] = React.useState(() => _parseLocalDate(customer.next_due_date));
  // Controlled month state supaya navigasi calendar tidak lompat dua bulan
  const [visibleMonth, setVisibleMonth] = React.useState(() => _parseLocalDate(customer.next_due_date));

  const handleSave = async () => {
    if (!selectedDate) return;
    setSaving(true);
    try {
      const iso = format(selectedDate, 'yyyy-MM-dd');
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // 1) NDD di masa lalu -> cari invoice Lunas periode yg sama & minta konfirmasi (Delta #76: pembayaran ikut dibatalkan)
      const today = new Date(); today.setHours(0, 0, 0, 0);
      let target = null;
      if (selectedDate < today) {
        const pm = selectedDate.getMonth() + 1;
        const py = selectedDate.getFullYear();
        try {
          const invRes = await axios.get(
            `${API}/api/v2/invoices?customer_id=${customer.id}&per_page=50`,
            { headers }
          );
          const invoices = invRes.data?.data || [];
          target = invoices.find(inv =>
            String(inv.period_month) === String(pm) &&
            String(inv.period_year) === String(py) &&
            (inv.status || '').toLowerCase() === 'paid'
          ) || null;
        } catch (invErr) {
          console.warn('Cek invoice gagal:', invErr?.message);
        }
        if (target && !window.confirm(
          `Invoice ${target.invoice_number} berstatus LUNAS.\n\nJatuh tempo di masa lalu akan mengembalikan invoice ini ke Belum Lunas dan PEMBAYARANNYA DIBATALKAN (jurnal kas dibalik).\n\nLanjutkan?`
        )) {
          return;
        }
      }

      // 2) Update customer next_due_date
      await axios.put(`${API}/api/v2/customers/${customer.id}`, { next_due_date: iso }, { headers });

      // 3) Revert invoice Lunas periode yg sama (backend membatalkan pembayaran + balik jurnal)
      if (target) {
        try {
          const r = await axios.put(`${API}/api/v2/invoices/${target.id}`, { status: 'pending' }, { headers });
          toast.info(r.data?.message || `Invoice ${target.invoice_number} dikembalikan ke Belum Lunas`);
        } catch (invErr) {
          toast.error(invErr?.response?.data?.detail || 'Gagal mengembalikan invoice ke Belum Lunas');
        }
      }

      // Optimistic local state update (2026-07-28) - no more window.location.reload()
      if (typeof updateCustomerLocal === 'function') {
        const patch = { next_due_date: iso };
        // Kalau NDD di masa lalu, sinkron ke belum_bayar juga di state lokal
        const _today = new Date(); _today.setHours(0, 0, 0, 0);
        if (selectedDate < _today) {
          patch.payment_status = 'belum_bayar';
        }
        updateCustomerLocal(customer.id, patch);
      }
      toast.success(`Jatuh tempo ${customer.name} → ${format(selectedDate, 'dd/MM/yyyy')}`);
      setOpen(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Gagal update jatuh tempo';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/40 text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-300 transition-colors ml-1"
          title="Ubah jatuh tempo"
          data-testid={`edit-due-date-${customer.id}`}
        >
          <CalendarDays className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <div className="mb-2 pb-2 border-b border-slate-200 dark:border-slate-700">
          <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">Ubah Jatuh Tempo</p>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{customer.name} ({customer.account_number})</p>
        </div>
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(d) => {
            if (d) {
              setSelectedDate(d);
              setVisibleMonth(d);
            }
          }}
          month={visibleMonth}
          onMonthChange={setVisibleMonth}
          initialFocus
        />
        <div className="flex gap-2 mt-3">
          <Button
            size="sm"
            variant="outline"
            className="flex-1 h-8 text-xs"
            onClick={() => setOpen(false)}
            disabled={saving}
            data-testid={`cancel-due-date-${customer.id}`}
          >
            Batal
          </Button>
          <Button
            size="sm"
            className="flex-1 h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
            onClick={handleSave}
            disabled={saving || !selectedDate}
            data-testid={`save-due-date-${customer.id}`}
          >
            {saving ? 'Menyimpan...' : 'Simpan'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Format durasi online relatif (mis. "3 jam 12 menit", "2 hari 4 jam")
const formatOnlineDuration = (sessionStart) => {
  if (!sessionStart) return null;
  try {
    const start = new Date(sessionStart.replace(' ', 'T'));
    if (isNaN(start.getTime())) return null;
    const diffMs = Date.now() - start.getTime();
    if (diffMs < 0) return null;
    const totalMin = Math.floor(diffMs / 60000);
    const days = Math.floor(totalMin / 1440);
    const hours = Math.floor((totalMin % 1440) / 60);
    const mins = totalMin % 60;
    if (days > 0) return `${days} hari ${hours} jam`;
    if (hours > 0) return `${hours} jam ${mins} menit`;
    if (mins > 0) return `${mins} menit`;
    return 'baru saja';
  } catch { return null; }
};

const CustomerDesktopRow = React.memo(function CustomerDesktopRow({
  customer,
  index,
  isSelected,
  acsStatus,
  mikrotikLoadingThis,
  branchLabel,
  handleSelectCustomer,
  handleEdit,
  handleDelete,
  handleResetPassword,
  handleTogglePaymentStatus,
  handleRecordPayment,
  handleToggleActive,
  handleDisableCustomer,
  handleEnableCustomer,
  handleIsolirCustomer,
  handleUnisolirCustomer,
  navigate,
  onViewDetail,
  handleMarkDebt,
  handleClearDebt,
  currentUserRole,
  updateCustomerLocal,
  handleRemotePPPoE,
  remotePPPoELoading,
  handleEnableWanWeb,
  enableWanWebLoading,
}) {
  const { user: _authUserHF } = useAuth();
  const [printOpen, setPrintOpen] = useState(false);
  const _isTeknisi = (_authUserHF?.role || '').toLowerCase() === 'teknisi';
  const _isOwner = (_authUserHF?.role || '').toLowerCase() === 'owner';  // OWNER_MENU_GUARD_v1_2026_08_22
  const productClass = acsStatus?.product_class || customer.ont_model;
  const acsOnline = acsStatus?.online || customer.acs_status === 'online';
  const rxPower = acsStatus?.rx_power || customer.ont_rx_power;
  const payStatus = customer.payment_status_computed || 'BELUM LUNAS';

  return (
    <>
    <tr data-customer-row-id={customer.id} className={`border-b border-slate-100 hover:bg-gradient-to-r hover:from-indigo-50/50 hover:to-purple-50/50 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/30 transition-all duration-200 ${index % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-slate-50/30 dark:bg-slate-800/50'}`}>
      <td className="py-2 pl-6 pr-2">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => handleSelectCustomer(customer.id)}
          className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
        />
      </td>
      <td className="py-2 px-2">
        {(() => {
          const isHotspot = (customer.connection_type || '').toLowerCase() === 'hotspot';
          if (customer.is_online) {
            const tip = (() => {
              const dur = formatOnlineDuration(customer.session_start);
              const base = isHotspot ? 'Online — Hotspot aktif' : 'Online — Wi-Fi terhubung';
              return dur ? `Online sejak ${dur} yang lalu` : base;
            })();
            return (
              <span
                title={tip}
                data-testid={`conn-online-${customer.id}`}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold tracking-wide border ${isHotspot ? 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-800' : 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800'}`}
              >
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${isHotspot ? 'bg-sky-500' : 'bg-green-500'} animate-pulse`}></span>
                ONLINE
              </span>
            );
          }
          // Offline
          return (
            <span
              title={isHotspot ? 'Offline — Hotspot tidak aktif' : 'Offline — Wi-Fi terputus'}
              data-testid={`conn-offline-${customer.id}`}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold tracking-wide border bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700`}
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-400 dark:bg-slate-500"></span>
              OFFLINE
            </span>
          );
        })()}
      </td>
      <td className="py-2 px-2">
        <div className="flex items-center gap-1.5">
          {/* 2026-06-05: Map indicator. Hijau bulat = sudah ada koordinat (titik
              ordinat), abu-abu coret = belum di-set. Klik untuk buka Google Maps. */}
          {(() => {
            const lat = parseFloat(customer.latitude);
            const lng = parseFloat(customer.longitude);
            const hasMap = !isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0);
            return hasMap ? (
              <a
                href={`https://www.google.com/maps?q=${lat},${lng}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                data-testid={`map-badge-${customer.id}`}
                title={`Lokasi sudah di-set: ${lat.toFixed(5)}, ${lng.toFixed(5)}. Klik buka Google Maps.`}
                className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-slate-500 dark:text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
              >
                <MapPin size={13} />
              </a>
            ) : (
              <span
                data-testid={`map-badge-empty-${customer.id}`}
                title="Koordinat belum di-set — klik tombol Edit untuk menambahkan latitude & longitude"
                className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-amber-500 dark:text-amber-400"
              >
                <MapPinOff size={13} />
              </span>
            );
          })()}

          <div
            className="font-medium text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:underline cursor-pointer transition-all flex items-center gap-1 group min-w-0"
            onClick={() => navigate(`/app/customers/${customer.id}`)}
            title="Click untuk lihat detail customer"
          >
            {customer.name}
            <Eye size={12} className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
          </div>        </div>
      </td>
      <td className="py-2 px-2 text-xs text-slate-600 dark:text-white hidden 2xl:table-cell max-w-[110px] leading-tight" title={customer.owner_name || customer.owner || '-'}><span className="line-clamp-2 break-words">{customer.owner_name || customer.owner || '-'}</span></td>
      <td className="py-2 px-2 text-xs text-slate-600 dark:text-white hidden 2xl:table-cell max-w-[100px] truncate" title={branchLabel}>{branchLabel}</td>
      <td className="py-2 px-2 text-xs text-slate-600 dark:text-white font-mono whitespace-nowrap">{customer.account_number}</td>
      <td className="py-2 px-2 text-xs text-slate-800 dark:text-slate-200 whitespace-nowrap">
        {(() => {
          const pt = String(customer.payment_type || 'prepaid').toLowerCase();
          const isPost = pt === 'postpaid';
          return (
            <span
              className={
                'inline-flex items-center px-1.5 py-0.5 mr-1.5 rounded text-[10px] font-bold uppercase tracking-wide align-middle ' +
                (isPost
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                  : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300')
              }
              title={isPost ? 'Postpaid' : 'Prepaid'}
              data-testid={`payment-type-badge-${customer.id}`}
            >
              {isPost ? 'POST' : 'PRE'}
            </span>
          );
        })()}
        {customer.service_plan}
      </td>
      <td className="py-2 px-2 text-xs hidden 2xl:table-cell font-mono whitespace-nowrap">
        {(() => {
          // ACTIVEPPPOEIP_2026-09-04: pakai IP realtime dari mikrotik_pppoe_active (bulk-joined di backend) sebagai sumber utama, fallback ke current_ip / last_ip_address
          const ip = customer.active_pppoe_ip || customer.current_ip || customer.last_ip_address;
          if (!ip) return <span className="text-slate-400">-</span>;
          const isPppoe = String(customer.connection_type || '').toLowerCase() === 'pppoe';
          const canRemote = isPppoe && customer.is_online && (customer.pppoe_username || ip);
          const isLoading = !!(remotePPPoELoading && remotePPPoELoading[customer.id]);
          const title = (customer.installation_date ? 'Registrasi: ' + customer.installation_date + ' - ' : '') + (canRemote ? 'Klik untuk Remote ONT' : (!customer.is_online ? 'Customer offline' : 'Bukan PPPoE'));
          // LOCAL_IP_BUTTON_2026-02-13: tombol buka IP local langsung tanpa proxy
          // — untuk teknisi on-site yang bisa route ke jaringan 172.16.x.
          //   v3 (2026-02-13): outline-only style biar konsisten dengan icon
          //   lain di tabel (icon aksi Mikrotik, dll). Router icon stroke only.
          const showLocalBtn = canRemote;
          const localBtn = showLocalBtn ? (
            <a
              href={`http://${ip}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              title={`Akses lokal (LAN/VPN): http://${ip}`}
              className="inline-flex items-center justify-center text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors"
              data-testid={"local-ont-ip-link-" + customer.id}
            >
              <Router className="w-4 h-4" strokeWidth={2} />
            </a>
          ) : null;
          if (!canRemote || !handleRemotePPPoE) {
            return (
              <span className="inline-flex items-center gap-1.5">
                {localBtn}
                <span className="text-slate-500 dark:text-slate-300" title={title}>{ip}</span>
              </span>
            );
          }
          return (
            <span className="inline-flex items-center gap-1.5">
              {localBtn}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleRemotePPPoE(customer); }}
                disabled={isLoading}
                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline disabled:opacity-50 disabled:cursor-wait transition-colors"
                title={title}
                data-testid={"remote-ont-ip-link-" + customer.id}
              >
                <span>{ip}</span>
                {isLoading ? (
                  <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/></svg>
                ) : (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
                )}
              </button>
            </span>
          );
        })()}
      </td>
      <td className="py-2 px-2 text-xs hidden xl:table-cell">
        {(() => {
          if (!customer.next_due_date) return (
            <div className="flex items-center gap-1.5">
              <span className="text-slate-400">-</span>
              {!_isTeknisi && <QuickDueDatePicker customer={customer} updateCustomerLocal={updateCustomerLocal} />}
            </div>
          );
          const dueDate = new Date(customer.next_due_date);
          dueDate.setHours(23, 59, 59, 999);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const dueDateStart = new Date(customer.next_due_date);
          dueDateStart.setHours(0, 0, 0, 0);
          const daysLeft = Math.ceil((dueDateStart - today) / (1000 * 60 * 60 * 24));

          // Priority indicator: H+0/lewat=merah, H-1/H-2=oranye, H-3=hijau, lainnya=normal
          let dotClass = '';
          let textClass = 'text-slate-600 dark:text-white';
          let label = null;
          if (daysLeft < 0) {
            dotClass = 'bg-red-500';
            textClass = 'text-red-600 dark:text-red-400 font-semibold';
            label = `Telat ${Math.abs(daysLeft)}hr`;
          } else if (daysLeft === 0) {
            dotClass = 'bg-red-500 animate-pulse';
            textClass = 'text-red-600 dark:text-red-400 font-semibold';
            label = 'Hari ini';
          } else if (daysLeft === 1) {
            dotClass = 'bg-orange-500';
            textClass = 'text-orange-600 dark:text-orange-400 font-semibold';
            label = 'H-1';
          } else if (daysLeft === 2) {
            dotClass = 'bg-orange-400';
            textClass = 'text-orange-500 dark:text-orange-300 font-medium';
            label = 'H-2';
          } else if (daysLeft === 3) {
            dotClass = 'bg-emerald-500';
            textClass = 'text-emerald-600 dark:text-emerald-400 font-medium';
            label = 'H-3';
          }

          return (
            <div className="flex items-center gap-1.5 whitespace-nowrap">
              {dotClass && <span className={`inline-block w-2 h-2 rounded-full ${dotClass} flex-shrink-0`} title={label || ''} />}
              <span className={textClass}>{format(dueDate, 'dd/MM/yyyy')}</span>
              {label && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-md ${
                  daysLeft <= 0 ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' :
                  daysLeft <= 2 ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' :
                  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                }`}>{label}</span>
              )}
              {!_isTeknisi && <QuickDueDatePicker customer={customer} updateCustomerLocal={updateCustomerLocal} />}
            </div>
          );
        })()}
      </td>
      <td className="py-2 px-2">
        <div className="flex flex-col items-start gap-1">
        {payStatus === 'LUNAS' ? (
          <Badge
            data-testid={`pay-status-lunas-${customer.id}`}
            className="inline-flex items-center justify-center gap-1.5 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-[10px] py-0.5 px-1.5 font-medium whitespace-nowrap"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
            Lunas
          </Badge>
        ) : payStatus === 'TERLAMBAT' ? (
          <Badge
            data-testid={`pay-status-terlambat-${customer.id}`}
            className="inline-flex items-center justify-center gap-1.5 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-[10px] py-0.5 px-1.5 font-medium whitespace-nowrap"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" aria-hidden="true" />
            Terlambat
          </Badge>
        ) : (
          <Badge
            data-testid={`pay-status-belum-${customer.id}`}
            className="inline-flex items-center justify-center gap-1.5 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-[10px] py-0.5 px-1.5 font-medium whitespace-nowrap"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" aria-hidden="true" />
            Belum Lunas
          </Badge>
        )}
        {(() => {
          // 2026-07-30: HUTANG badge — visible when debt_deadline set & still in future
          const dd = customer.debt_deadline;
          if (!dd) return null;
          try {
            const _d = new Date(dd);
            const _today = new Date(); _today.setHours(0,0,0,0);
            if (isNaN(_d.getTime()) || _d < _today) return null;
            const _label = format(_d, 'dd MMM');
            return (
              <Badge
                data-testid={`debt-badge-${customer.id}`}
                title={`Dalam masa tenggang. Deadline pelunasan: ${format(_d,'dd MMM yyyy')}. Auto-isolir bila lewat.`}
                className="inline-flex items-center gap-1 bg-amber-50 dark:bg-amber-900/30 border border-amber-400 text-amber-800 dark:text-amber-200 text-[10px] py-0.5 px-1.5 font-semibold whitespace-nowrap"
              >
                <span aria-hidden="true">🕐</span>
                MASA TENGGANG s/d {_label}
              </Badge>
            );
          } catch (_e) { return null; }
        })()}
        <WaInactiveBadge customer={customer} />
        </div>
      </td>
      {/* ONT (gabungan: Signal + ACS icon + Model) */}
      <td className="py-2 px-2 whitespace-nowrap">
        {!customer.wan_device_id ? (
          <span className="text-slate-400 text-xs">-</span>
        ) : (
          <div className="flex flex-col gap-0.5">
            {productClass && (
              <span className="text-xs font-medium text-teal-600 dark:text-teal-400" title={productClass}>
                {productClass}
              </span>
            )}
            <div className="flex items-center gap-1.5">
              {(() => {
                if (!acsOnline) return <span className="text-[10px] text-slate-400 dark:text-slate-500">— dBm</span>;
                if (!rxPower) return <span className="text-[10px] text-slate-400 dark:text-slate-500">— dBm</span>;
                const rxValue = parseFloat(rxPower);
                let colorClass = "text-green-600/80 dark:text-green-400/80";
                if (rxValue < -25) colorClass = "text-red-600/80 dark:text-red-400/80";
                else if (rxValue < -20) colorClass = "text-yellow-600/80 dark:text-yellow-400/80";
                return <span className={`text-[10px] font-medium ${colorClass}`}>{rxPower} dBm</span>;
              })()}
            </div>
          </div>
        )}
      </td>
      {/* MikroTik */}
      <td className="py-2 px-2 hidden xl:table-cell">
        <div className="flex flex-col gap-1">
          {customer.mikrotik_user ? (
            <>
              <div className="flex items-center gap-1 flex-wrap">
                {customer.account_status === 'disabled' ? (
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 border-red-300 bg-transparent text-[10px] h-5 px-1.5"
                    onClick={() => handleEnableCustomer(customer.id)} disabled={mikrotikLoadingThis} title="Account Disabled - Klik untuk Enable">
                    <ShieldOff className="w-2.5 h-2.5" strokeWidth={1.75} />
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 border-slate-200 dark:border-slate-700 bg-transparent text-[10px] h-5 px-1.5"
                    onClick={() => handleDisableCustomer(customer.id)} disabled={mikrotikLoadingThis} title="Account Active - Klik untuk Disable">
                    <Shield className="w-2.5 h-2.5" strokeWidth={1.75} />
                  </Button>
                )}
                {customer.is_isolated ? (
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 border-red-300 bg-transparent text-[10px] h-5 px-1.5"
                    onClick={() => handleUnisolirCustomer(customer.id)} disabled={mikrotikLoadingThis} title="Klik untuk Unisolir (Buka Blokir Internet)">
                    <Ban className="w-2 h-2" strokeWidth={1.75} />
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 border-slate-200 dark:border-slate-700 bg-transparent text-[10px] h-5 px-1.5"
                    onClick={() => handleIsolirCustomer(customer.id)} disabled={mikrotikLoadingThis} title="Klik untuk Isolir (Blokir Internet)">
                    <CheckCircle className="w-2.5 h-2.5" strokeWidth={1.75} />
                  </Button>
                )}
              </div>
              <span className="text-[9px] text-blue-600 dark:text-blue-400 truncate max-w-[120px]" title={customer.mikrotik_user}>
                {customer.mikrotik_user}
              </span>
            </>
          ) : (customer.connection_type === 'static' && (customer.current_ip || customer.current_mac)) ? (
            <div className="text-[9px] flex flex-col gap-0.5">
              <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700">
                <span className="font-bold text-blue-700 dark:text-blue-300">IP</span>
                <span className="font-mono text-blue-900 dark:text-blue-100" title={customer.current_ip}>
                  {customer.current_ip || '-'}
                </span>
              </div>
              {customer.current_mac && (
                <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                  <span className="font-bold text-slate-600 dark:text-slate-300">MAC</span>
                  <span className="font-mono text-slate-700 dark:text-slate-200 truncate max-w-[110px]" title={customer.current_mac}>
                    {customer.current_mac}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <span className="text-[9px] text-blue-600 dark:text-blue-400">No MikroTik</span>
          )}
        </div>
      </td>
      <td className="py-2 pl-2 pr-6 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-amber-100 dark:hover:bg-amber-900/30 text-amber-500 dark:text-amber-400 ml-auto" title="Aksi" data-testid={`desktop-actions-${customer.id}`}>
              <Sparkles className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => (onViewDetail ? onViewDetail(customer.id) : navigate(`/app/customers/${customer.id}`))} data-testid={`row-view-detail-${customer.id}`}>
              <Eye className="w-4 h-4 mr-2 text-indigo-500" />Lihat Detail
            </DropdownMenuItem>
            {!_isTeknisi && (<>
            {!_isOwner && (
            <DropdownMenuItem onClick={() => navigate(`/app/invoices?customer=${encodeURIComponent(customer.account_number || customer.name)}`)}>
              <FileText className="w-4 h-4 mr-2 text-teal-500" />Edit Tagihan
            </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => setPrintOpen(true)} data-testid={`row-print-invoice-${customer.id}`}>
              <Printer className="w-4 h-4 mr-2 text-indigo-500" />Print Invoice
            </DropdownMenuItem>
            {!_isOwner && (<>
            <DropdownMenuItem onClick={() => handleEdit(customer)}>
              <Edit className="w-4 h-4 mr-2 text-blue-500" />Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleResetPassword(customer)}>
              <Key className="w-4 h-4 mr-2 text-amber-500" />Reset Password
            </DropdownMenuItem>
            </>)}
            {handleRemotePPPoE && String(customer.connection_type || '').toLowerCase() === 'pppoe' && customer.is_online && (customer.active_pppoe_ip || customer.last_ip_address || customer.pppoe_username) && (
              <DropdownMenuItem
                onClick={() => handleRemotePPPoE(customer)}
                disabled={!!(remotePPPoELoading && remotePPPoELoading[customer.id])}
                className="text-cyan-600 dark:text-cyan-400"
                data-testid={`row-remote-ont-${customer.id}`}
                title={`Buka web ONT via socat proxy (${customer.active_pppoe_ip || customer.last_ip_address || customer.pppoe_username})`}
              >
                <Wifi className="w-4 h-4 mr-2" />{(remotePPPoELoading && remotePPPoELoading[customer.id]) ? 'Membuka...' : 'Remote ONT'}
              </DropdownMenuItem>
            )}
            {(() => {
              // 2026-02: Rule H-5 — prepaid + non admin/super_admin baru bisa bayar 5 hari sebelum jatuh tempo
              const _role = String(_authUserHF?.role || '').toLowerCase();
              const _isAdmin = _role === 'admin' || _role === 'super_admin' || _role === 'superadmin';
              const _isPrepaid = String(customer.payment_type || 'prepaid').toLowerCase() === 'prepaid';
              let _blocked = false, _openFrom = null, _daysLeft = 0;
              if (_isPrepaid && !_isAdmin && customer.next_due_date) {
                const _due = new Date(String(customer.next_due_date).slice(0,10) + 'T00:00:00');
                if (!isNaN(_due)) {
                  const _open = new Date(_due); _open.setDate(_open.getDate() - 5);
                  const _t = new Date(); _t.setHours(0,0,0,0);
                  if (_t < _open) {
                    _blocked = true;
                    _openFrom = _open;
                    _daysLeft = Math.ceil((_open - _t) / (24*60*60*1000));
                  }
                }
              }
              const _title = _blocked
                ? `Pembayaran tersedia mulai ${_openFrom.toLocaleDateString('id-ID')} (${_daysLeft} hari lagi). Hubungi Admin bila mendesak.`
                : 'Catat pembayaran';
              return (
                <DropdownMenuItem
                  onClick={() => { if (_blocked) { return; } handleRecordPayment(customer); }}
                  disabled={_blocked}
                  className={_blocked ? 'text-slate-400 cursor-not-allowed pointer-events-none' : 'text-emerald-600 font-medium'}
                  data-testid={`customer-row-bayar-${customer.id}`}
                  title={_title}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />Bayar{_blocked ? ` (${_daysLeft}h lagi)` : ''}
                </DropdownMenuItem>
              );
            })()}
            {(!_isOwner || payStatus !== 'LUNAS') && (
            <DropdownMenuItem
              onClick={() => handleTogglePaymentStatus(customer)}
              className={payStatus === 'LUNAS' ? "text-orange-600" : "text-emerald-600"}
            >
              {payStatus === 'LUNAS' ? (
                <><CircleDollarSign className="w-4 h-4 mr-2" />Ubah ke Belum Bayar</>
              ) : (
                <><CheckCircle className="w-4 h-4 mr-2" />Ubah ke Lunas</>
              )}
            </DropdownMenuItem>
            )}
            {/* 2026-07-30: Berhutang controls (admin & kepala_cabang only) */}
            {(['admin','super_admin','kepala_cabang'].includes((currentUserRole||'').toLowerCase())) && (
              customer.debt_deadline ? (
                <DropdownMenuItem
                  onClick={() => handleClearDebt && handleClearDebt(customer)}
                  className="text-slate-600"
                  data-testid={`row-clear-debt-${customer.id}`}
                >
                  <CircleDollarSign className="w-4 h-4 mr-2" />Hapus Masa Tenggang
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={() => handleMarkDebt && handleMarkDebt(customer)}
                  className="text-amber-600"
                  data-testid={`row-mark-debt-${customer.id}`}
                >
                  <CircleDollarSign className="w-4 h-4 mr-2" />Tandai Masa Tenggang
                </DropdownMenuItem>
              )
            )}
            </>)}
            {!_isTeknisi && !_isOwner && (<>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                const isActive = (customer.account_status !== 'disabled');
                if (isActive) {
                  if (window.confirm('Nonaktifkan customer? PPPoE akan diputus dan tidak bisa dial sampai diaktifkan kembali.')) {
                    handleDisableCustomer(customer.id);
                  }
                } else {
                  handleEnableCustomer(customer.id);
                }
              }}
              className={(customer.account_status !== 'disabled') ? "text-orange-600" : "text-emerald-600"}
            >
              {(customer.account_status !== 'disabled') ? (
                <><ToggleLeft className="w-4 h-4 mr-2" />Nonaktifkan</>
              ) : (
                <><ToggleRight className="w-4 h-4 mr-2" />Aktifkan</>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleDelete(customer.id)} className="text-red-600">
              <Trash2 className="w-4 h-4 mr-2" />Hapus
            </DropdownMenuItem>
            </>)}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
    <PrintInvoiceModal isOpen={printOpen} onClose={() => setPrintOpen(false)} customer={customer} />
    </>
  );
});


/**
 * Desktop (>= lg) full table view for customers list with sortable columns.
 */
const CustomerDesktopTable = ({
  isLoading,
  currentCustomers,
  acsDevicesStatus,
  selectedCustomers,
  sortConfig,
  mikrotikLoading,
  handleSelectCustomer,
  handleSelectAll,
  handleSort,
  handleEdit,
  handleDelete,
  handleResetPassword,
  handleTogglePaymentStatus,
  handleRecordPayment,
  handleToggleActive,
  handleDisableCustomer,
  handleEnableCustomer,
  handleIsolirCustomer,
  handleUnisolirCustomer,
  getBranchName,
  navigate,
  onViewDetail,
  handleMarkDebt,
  handleClearDebt,
  currentUserRole,
  t,
  updateCustomerLocal,
  handleRemotePPPoE,
  remotePPPoELoading,
  handleEnableWanWeb,
  enableWanWebLoading,
}) => {
  return (
    <div className="hidden lg:block" data-testid="customers-desktop-table">
      <div className="overflow-auto rounded-lg border border-slate-200 [&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]" style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 280px)' }}>
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm text-slate-500">Memuat data...</span>
            </div>
          </div>
        ) : currentCustomers.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <div className="text-4xl mb-2">📋</div>
            <p className="font-medium">Tidak ada data pelanggan</p>
            <p className="text-sm">Coba ubah filter atau kata kunci pencarian</p>
          </div>
        ) : (
          <table className="w-full min-w-[1060px]">
            <thead className="bg-slate-100 dark:bg-slate-800 border-b-2 border-indigo-100 dark:border-indigo-800 sticky top-0 z-20 shadow-sm">
              <tr>
                <th className="text-left py-2 pl-6 pr-2 text-xs font-semibold text-slate-600 w-14 bg-slate-100 dark:bg-slate-800">
                  <input
                    type="checkbox"
                    checked={selectedCustomers.length === currentCustomers.length && currentCustomers.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                </th>
                <SortHeader sortConfig={sortConfig} onSort={handleSort} sortKey="connection_status" label={t('connection')} />
                <SortHeader sortConfig={sortConfig} onSort={handleSort} sortKey="name" label={t('customer')} />
                <SortHeader sortConfig={sortConfig} onSort={handleSort} sortKey="owner" label={t('owner')} className="hidden 2xl:table-cell" />
                <SortHeader sortConfig={sortConfig} onSort={handleSort} sortKey="branch_id" label={t('branches')} className="hidden 2xl:table-cell" />
                <SortHeader sortConfig={sortConfig} onSort={handleSort} sortKey="account_number" label={t('accountNo')} />
                <SortHeader sortConfig={sortConfig} onSort={handleSort} sortKey="service_plan" label={t('packagePlan')} className="min-w-[100px]" />
                <SortHeader sortConfig={sortConfig} onSort={handleSort} sortKey="current_ip" label="IP PPPoE" className="hidden 2xl:table-cell" />
                <SortHeader sortConfig={sortConfig} onSort={handleSort} sortKey="due_date" label="Jatuh Tempo" className="hidden xl:table-cell" />
                <SortHeader sortConfig={sortConfig} onSort={handleSort} sortKey="payment_status_computed" label={t('paymentStatus')} />

                <th data-testid="sort-acs-signal" onClick={() => handleSort('_acs_signal')} className="text-left py-2 px-2 text-xs font-semibold text-teal-600 dark:text-teal-400 bg-slate-100 dark:bg-slate-800 cursor-pointer hover:bg-slate-200 transition-colors">
                  <div className="flex items-center gap-1 whitespace-nowrap">
                    <Server className="w-3 h-3" />
                    ONT/ACS
                    {sortConfig.key === '_acs_signal' ? (sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 text-indigo-600" /> : <ArrowDown className="w-3 h-3 text-indigo-600" />) : <ArrowUpDown className="w-3 h-3 text-slate-400" />}
                  </div>
                </th>
                <th className="text-left py-2 px-2 text-xs font-semibold text-slate-600 dark:text-white bg-slate-100 dark:bg-slate-800 hidden xl:table-cell">
                  <div className="flex items-center gap-1 whitespace-nowrap">MikroTik</div>
                </th>                <th className="text-right py-2 pl-2 pr-6 text-xs font-semibold text-slate-600 dark:text-white bg-slate-100 dark:bg-slate-800">{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {currentCustomers.map((customer, index) => (
                <CustomerDesktopRow
                  key={customer.id ?? index}
                  customer={customer}
                  index={index}
                  isSelected={selectedCustomers.includes(customer.id)}
                  acsStatus={acsDevicesStatus[customer.wan_device_serial || customer.wan_device_id]}
                  mikrotikLoadingThis={!!mikrotikLoading[customer.id]}
                  branchLabel={getBranchName(customer.branch_id, customer.branch_name)}
                  handleSelectCustomer={handleSelectCustomer}
                  handleEdit={handleEdit}
                  handleDelete={handleDelete}
                  handleResetPassword={handleResetPassword}
                  handleTogglePaymentStatus={handleTogglePaymentStatus}
                  handleRecordPayment={handleRecordPayment}
                  handleToggleActive={handleToggleActive}
                  handleDisableCustomer={handleDisableCustomer}
                  handleEnableCustomer={handleEnableCustomer}
                  handleIsolirCustomer={handleIsolirCustomer}
                  handleUnisolirCustomer={handleUnisolirCustomer}
                  navigate={navigate}
                  onViewDetail={onViewDetail}
                  handleMarkDebt={handleMarkDebt}
                  handleClearDebt={handleClearDebt}
                  currentUserRole={currentUserRole}
                  updateCustomerLocal={updateCustomerLocal}
                  handleRemotePPPoE={handleRemotePPPoE}
                  remotePPPoELoading={remotePPPoELoading}
                  handleEnableWanWeb={handleEnableWanWeb}
                  enableWanWebLoading={enableWanWebLoading}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/**
 * Custom equality: re-render only when data props change.
 * sortConfig is critical here because it drives the header arrow indicators.
 * mikrotikLoading affects per-row button disabled states.
 */
const arePropsEqual = (prev, next) => (
  prev.isLoading === next.isLoading &&
  prev.currentCustomers === next.currentCustomers &&
  prev.acsDevicesStatus === next.acsDevicesStatus &&
  prev.selectedCustomers === next.selectedCustomers &&
  prev.sortConfig === next.sortConfig &&
  prev.mikrotikLoading === next.mikrotikLoading
);

export default React.memo(CustomerDesktopTable, arePropsEqual);
